import { useState } from 'react';
import { formatMinor } from '../../shared/utils/money';
import { exportToCsv } from '../../shared/utils/csv';
import type { Settlement, SettlementStatus } from '../settlements/types';

interface Props {
  settlements: Settlement[];
}

const PAGE_SIZE = 10;

const STATUS_STYLES: Record<SettlementStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function sortBySettledAtDesc(items: Settlement[]): Settlement[] {
  return [...items].sort((a, b) => {
    if (a.settledAt === null && b.settledAt === null) return 0;
    if (a.settledAt === null) return 1;
    if (b.settledAt === null) return -1;
    return new Date(b.settledAt).getTime() - new Date(a.settledAt).getTime();
  });
}

export function SettlementHistoryTable({ settlements }: Props) {
  const [page, setPage] = useState(1);

  const sorted = sortBySettledAtDesc(settlements);
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, total);
  const pageItems = sorted.slice(startIndex, endIndex);

  function handleExport() {
    const rows = sorted.map((s) => ({
      Provider: s.provider,
      Amount: formatMinor(s.amount, s.currency),
      Status: s.status,
      Type: s.isInternational ? 'International' : 'Local',
      Settled: formatDate(s.settledAt),
      'Settlement ID': s.providerSettlementId,
    }));
    exportToCsv('settlements.csv', rows);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-[#28282A]">Settlement History</h2>
        <button
          onClick={handleExport}
          disabled={total === 0}
          className="text-sm font-medium px-3 py-1.5 rounded-md border border-gray-200 text-[#28282A] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Export CSV
        </button>
      </div>

      {total === 0 ? (
        <p className="text-sm text-[#3F3E42]">
          No gateway settlements yet. Funds settle directly to the merchant under split-at-source.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 font-semibold text-[#28282A]">Provider</th>
                  <th className="text-left py-2 font-semibold text-[#28282A]">Amount</th>
                  <th className="text-left py-2 font-semibold text-[#28282A]">Status</th>
                  <th className="text-left py-2 font-semibold text-[#28282A]">Type</th>
                  <th className="text-left py-2 font-semibold text-[#28282A]">Settled</th>
                  <th className="text-left py-2 font-semibold text-[#28282A]">Settlement ID</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100">
                    <td className="py-2 text-[#3F3E42]">{s.provider}</td>
                    <td className="py-2 font-medium text-[#28282A]">
                      {formatMinor(s.amount, s.currency)}
                    </td>
                    <td className="py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[s.status] ?? 'bg-gray-100 text-gray-600'}`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td className="py-2 text-[#3F3E42]">
                      {s.isInternational ? 'International' : 'Local'}
                    </td>
                    <td className="py-2 text-[#3F3E42]">{formatDate(s.settledAt)}</td>
                    <td
                      className="py-2 text-[#3F3E42] font-mono text-xs max-w-[160px] truncate"
                      title={s.providerSettlementId}
                    >
                      {s.providerSettlementId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-[#3F3E42]">
            <span>
              Showing {startIndex + 1}–{endIndex} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
